import React, { useState, useEffect, useRef, useCallback } from 'react';
import { summarizeConversation } from '../../services/api';

interface SummarizationDisplayProps {
  conversationHistory: string;
  sourceLanguage: string;
  targetLanguage: string;
  context?: string;
  translationProvider?: string;
  onSummaryGenerated?: (summary: string) => void;
  autoSummarize?: boolean; // Enable auto-summarization
  autoSummarizeThreshold?: number; // Minimum new characters to trigger (default: 500)
  autoSummarizeInterval?: number; // Time interval in seconds (default: 30)
}

const SummarizationDisplay: React.FC<SummarizationDisplayProps> = ({
  conversationHistory,
  sourceLanguage,
  targetLanguage,
  context,
  translationProvider = 'dashscope',
  onSummaryGenerated,
  autoSummarize = true,
  autoSummarizeThreshold = 500, // Summarize when 500+ new characters
  autoSummarizeInterval = 30, // Or every 30 seconds
}) => {
  const [summary, setSummary] = useState<string>('');
  const [summarizedLength, setSummarizedLength] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [autoSummarizeEnabled, setAutoSummarizeEnabled] = useState<boolean>(autoSummarize);
  const [lastAutoSummarizeTime, setLastAutoSummarizeTime] = useState<number>(Date.now());
  const [pendingSummarize, setPendingSummarize] = useState<boolean>(false);
  
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastLengthRef = useRef<number>(0);
  const summaryRef = useRef<string>('');
  const summarizedLengthRef = useRef<number>(0);
  const isLoadingRef = useRef<boolean>(false);
  const pendingSummarizeRef = useRef<boolean>(false);
  
  // Keep refs in sync with state
  useEffect(() => {
    summaryRef.current = summary;
  }, [summary]);
  
  useEffect(() => {
    summarizedLengthRef.current = summarizedLength;
  }, [summarizedLength]);
  
  useEffect(() => {
    isLoadingRef.current = isLoading;
  }, [isLoading]);
  
  useEffect(() => {
    pendingSummarizeRef.current = pendingSummarize;
  }, [pendingSummarize]);

  const performSummarization = useCallback(async (isAuto: boolean = false) => {
    if (!conversationHistory || !conversationHistory.trim()) {
      if (!isAuto) {
        setError('No conversation history to summarize');
      }
      return;
    }

    // Don't auto-summarize if already loading
    if (isLoadingRef.current) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Use refs to get latest values
      const currentSummary = summaryRef.current;
      const currentSummarizedLength = summarizedLengthRef.current;
      const isIncremental = currentSummary.trim().length > 0 && currentSummarizedLength > 0;
      
      const result = await summarizeConversation(
        conversationHistory,
        sourceLanguage,
        targetLanguage,
        targetLanguage, // summary language defaults to target language
        null, // model - use default
        translationProvider,
        context || null,
        isIncremental ? currentSummary : null, // existing summary for incremental mode
        isIncremental ? currentSummarizedLength : null // summarized length for incremental mode
      );
      
      setSummary(result.summary);
      setSummarizedLength(result.summarized_length);
      setLastAutoSummarizeTime(Date.now());
      setIsExpanded(true);
      setPendingSummarize(false);
      
      // Notify parent component of generated summary
      if (onSummaryGenerated) {
        onSummaryGenerated(result.summary);
      }
    } catch (err) {
      console.error('Summarization error:', err);
      if (!isAuto) {
        setError('Failed to generate summary. Please try again.');
      }
      setPendingSummarize(false);
    } finally {
      setIsLoading(false);
    }
  }, [
    conversationHistory,
    sourceLanguage,
    targetLanguage,
    translationProvider,
    context,
    onSummaryGenerated,
  ]);

  const handleSummarize = useCallback(() => {
    performSummarization(false);
  }, [performSummarization]);

  // Reset summary tracking when conversation history is cleared or significantly changed
  useEffect(() => {
    // If conversation history is shorter than what we've summarized, reset
    if (conversationHistory.length < summarizedLength) {
      setSummary('');
      setSummarizedLength(0);
      lastLengthRef.current = 0;
    }
  }, [conversationHistory, summarizedLength]);

  // Reset summary when target language changes (so user can summarize in new language)
  const prevTargetLanguageRef = useRef<string>(targetLanguage);
  useEffect(() => {
    // Only clear if target language actually changed (not on initial mount)
    if (prevTargetLanguageRef.current !== targetLanguage && prevTargetLanguageRef.current !== '') {
      if (summary.trim().length > 0) {
        // Clear existing summary when target language changes
        setSummary('');
        setSummarizedLength(0);
        lastLengthRef.current = 0;
        setIsExpanded(false);
        // Notify parent that summary was cleared
        if (onSummaryGenerated) {
          onSummaryGenerated('');
        }
      }
    }
    prevTargetLanguageRef.current = targetLanguage;
  }, [targetLanguage, summary, onSummaryGenerated]);

  // Auto-summarization logic
  useEffect(() => {
    if (!autoSummarizeEnabled || !conversationHistory || !conversationHistory.trim()) {
      return;
    }

    const currentLength = conversationHistory.length;
    const newContentLength = currentLength - summarizedLengthRef.current;
    const timeSinceLastSummarize = (Date.now() - lastAutoSummarizeTime) / 1000; // in seconds

    // Check if we should trigger auto-summarization
    const shouldTriggerByLength = newContentLength >= autoSummarizeThreshold;
    const shouldTriggerByTime = timeSinceLastSummarize >= autoSummarizeInterval && newContentLength > 0;

    if ((shouldTriggerByLength || shouldTriggerByTime) && !isLoadingRef.current && !pendingSummarizeRef.current) {
      setPendingSummarize(true);
      performSummarization(true);
    }

    // Set up interval check for time-based triggering
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    intervalRef.current = setInterval(() => {
      const currentLen = conversationHistory.length;
      const newLen = currentLen - summarizedLengthRef.current;
      const timeSince = (Date.now() - lastAutoSummarizeTime) / 1000;

      if (newLen > 0 && timeSince >= autoSummarizeInterval && !isLoadingRef.current && !pendingSummarizeRef.current) {
        setPendingSummarize(true);
        performSummarization(true);
      }
    }, 5000); // Check every 5 seconds

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [
    autoSummarizeEnabled,
    conversationHistory,
    autoSummarizeThreshold,
    autoSummarizeInterval,
    lastAutoSummarizeTime,
    performSummarization,
  ]);

  const hasContent = summary.trim().length > 0;

  return (
    <div className="bg-white rounded-lg shadow-md p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
            <svg
              className="w-5 h-5 text-primary-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            Conversation Summary
          </h3>
          {autoSummarizeEnabled && (
            <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full font-medium">
              Auto
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {hasContent && (
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="text-sm text-primary-500 hover:text-primary-600 transition-colors"
            >
              {isExpanded ? 'Collapse' : 'Expand'}
            </button>
          )}
          <button
            onClick={() => setAutoSummarizeEnabled(!autoSummarizeEnabled)}
            className={`text-xs px-2 py-1 rounded transition-colors ${
              autoSummarizeEnabled
                ? 'bg-green-100 text-green-700 hover:bg-green-200'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
            title={autoSummarizeEnabled ? 'Disable auto-summarization' : 'Enable auto-summarization'}
          >
            {autoSummarizeEnabled ? 'ON' : 'OFF'}
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
          {error}
        </div>
      )}

      {hasContent && isExpanded && (
        <div className="mb-3 p-4 bg-gray-50 rounded-md border border-gray-200">
          <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">
            {summary}
          </p>
        </div>
      )}

      {hasContent && !isExpanded && (
        <div className="mb-3">
          <p className="text-sm text-gray-500 line-clamp-2">
            {summary}
          </p>
        </div>
      )}

      <button
        onClick={handleSummarize}
        disabled={isLoading || !conversationHistory || !conversationHistory.trim()}
        className={`w-full px-4 py-2 rounded-lg font-semibold text-white transition-all ${
          isLoading
            ? 'bg-gray-400 cursor-not-allowed'
            : 'bg-primary-500 hover:bg-primary-600'
        } disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2`}
      >
        {isLoading ? (
          <>
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            <span>Đang tạo tóm tắt...</span>
          </>
        ) : (
          <>
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            <span>{hasContent ? 'Tóm tắt lại bằng ngôn ngữ mới' : 'Tóm tắt cuộc hội thoại'}</span>
          </>
        )}
      </button>

      {!conversationHistory || !conversationHistory.trim() ? (
        <p className="mt-2 text-xs text-gray-500 text-center">
          Start recording to generate conversation history
        </p>
      ) : (
        autoSummarizeEnabled && (
          <div className="mt-2 text-xs text-gray-500 text-center">
            <p>
              Auto-summarizes when {autoSummarizeThreshold}+ new characters are added
              {' or '}every {autoSummarizeInterval} seconds
            </p>
            {conversationHistory.length > summarizedLength && (
              <p className="mt-1 text-gray-400">
                {conversationHistory.length - summarizedLength} new characters pending
              </p>
            )}
          </div>
        )
      )}
    </div>
  );
};

export default SummarizationDisplay;

